import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Box,
} from '@mui/material';
import type { DialogProps } from '@mui/material';

export interface FormDialogProps
  extends Omit<DialogProps, 'onClose' | 'onSubmit' | 'children'> {
  /** Dialog title text. */
  title: string;
  /** Whether the dialog is open. */
  open: boolean;
  /** Called when the user requests to close (Cancel, backdrop, Escape). */
  onClose: () => void;
  /** Called when the form is submitted. Receives the form event. */
  onSubmit: (e: React.FormEvent) => void;
  /** Label for the submit button. Defaults to "Submit". */
  submitLabel?: string;
  /** Error message to display above the action buttons. */
  error?: string | null;
  /** When true, disables the submit button and shows "Saving…". */
  isPending?: boolean;
  /** Additional condition to disable the submit button (beyond isPending). */
  submitDisabled?: boolean;
  /** Form field content. */
  children: React.ReactNode;
}

/**
 * Shared dialog wrapper for CRUD forms.
 *
 * Provides consistent layout, error display above action buttons,
 * and loading state on the submit button.
 *
 * Used in two ways:
 * 1. As the root of a dedicated form component (e.g. LocationForm) — the form
 *    component owns state, handles preventDefault, and calls a domain onSubmit(data).
 * 2. Directly in a page (e.g. ManufacturersPage) — for simple single-field dialogs
 *    where a dedicated form component would be overkill.
 *
 * MUI DialogProps (e.g. TransitionProps) can be passed through via the rest spread.
 */
export const FormDialog: React.FC<FormDialogProps> = ({
  title,
  open,
  onClose,
  onSubmit,
  submitLabel = 'Submit',
  error,
  isPending = false,
  submitDisabled = false,
  children,
  maxWidth = 'sm',
  fullWidth = true,
  ...dialogProps
}) => {
  return (
    <Dialog
      open={open}
      onClose={isPending ? undefined : onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      {...dialogProps}
    >
      <form onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); if (!isPending && !submitDisabled) onSubmit(e); }}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>{children}</DialogContent>
        <DialogActions
          sx={{
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: 1,
            px: 3,
            pb: 2,
          }}
        >
          {error && (
            <Alert severity="error" sx={{ width: '100%' }}>
              {error}
            </Alert>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isPending || submitDisabled}
            >
              {isPending ? 'Saving\u2026' : submitLabel}
            </Button>
          </Box>
        </DialogActions>
      </form>
    </Dialog>
  );
};
